import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { TenantService } from '@vsolv/core/multi-tenant/web';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { SecurityService } from '@wsphere/staff/web';
import {
  FeatureFlagsService,
  NEW_CLAIMS_FLOW_FLAG,
  TenantFeature,
  TenantFeatureService,
} from '@wsphere/warranties/web';
import { firstValueFrom, map, of, Subject, Subscription, switchMap, takeUntil, timer } from 'rxjs';
import { catchError, filter, take, tap } from 'rxjs/operators';
import { FEATURE_FLAGS_CONFIG } from './feature-flags.config';

type DynamicRatingBackfillStatus = 'idle' | 'processing' | 'completed' | 'failed';

interface DynamicRatingBackfillState {
  status: DynamicRatingBackfillStatus;
  startedAt?: string;
  completedAt?: string;
  error?: string;
}

interface FeatureFlagsResponse {
  feature_flags: Record<string, boolean>;
  dynamic_rating_backfill?: DynamicRatingBackfillState;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-web-feature-flags-page',
  templateUrl: './feature-flags.page.html',
  styles: [
    `
      @keyframes backfill-progress {
        0% {
          transform: translateX(-100%) scaleX(0.35);
        }
        50% {
          transform: translateX(0%) scaleX(0.65);
        }
        100% {
          transform: translateX(100%) scaleX(0.35);
        }
      }

      .backfill-progress-bar {
        animation: backfill-progress 1.6s ease-in-out infinite;
        transform-origin: left center;
      }
    `,
  ],
  standalone: false,
})
export class FeatureFlagsPage implements OnInit, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);

  private onDestroy$ = new Subject<void>();
  private dynamicRatingBackfillPollingSubscription: Subscription | null = null;
  private dynamicRatingElapsedInterval: ReturnType<typeof setInterval> | null = null;
  private dynamicRatingWasEnabled = false;

  private readonly BACKFILL_POLL_INTERVAL_MS = 2000;
  private readonly BACKFILL_POLL_MAX_ATTEMPTS = 150;

  saving = false;
  loading = true;
  dynamicRatingBackfillProcessing = false;
  dynamicRatingBackfillStatus: DynamicRatingBackfillStatus = 'idle';
  dynamicRatingBackfillStartedAt?: string;
  dynamicRatingBackfillError?: string;
  dynamicRatingBackfillElapsed = '';
  dynamicRatingBackfillShowSuccess = false;
  private dynamicRatingBackfillSuccessTimeout: ReturnType<typeof setTimeout> | null = null;

  canEditFlags$ = this.securitySvc.globalDistributors$.pipe(
    switchMap(async globalDist => {
      return this.securitySvc.hasAccess('stf_EditSettings', globalDist?.map(dist => dist.permissionKey) || null);
    })
  );

  readonly config = FEATURE_FLAGS_CONFIG;

  // Custom roles and the new claims flow change how the whole tenant works, so only a full administrator may toggle them.
  isFullAdmin$ = this.securitySvc.globalDistributors$.pipe(switchMap(() => this.securitySvc.hasAccess('*', null)));

  flagsForm = this.formBuilder.group({
    declarationPage: [false],
    dynamicRating: [false],
    customRoles: [false],
    batchWarrantyPayments: [false],
    claimZeroPay: [false],
    newClaimsFlow: [false],
  });

  constructor(
    private http: HttpClient,
    private formBuilder: FormBuilder,
    private toastSvc: ToastService,
    private securitySvc: SecurityService,
    private featureFlagsSvc: FeatureFlagsService,
    private tenantFeatureSvc: TenantFeatureService,
    private tenantSvc: TenantService
  ) {}

  ngOnInit(): void {
    this.loadFlags();
  }

  ngOnDestroy(): void {
    this.stopDynamicRatingBackfillPolling();
    this.stopDynamicRatingElapsedTimer();
    this.clearDynamicRatingSuccessBanner();
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  async loadFlags() {
    this.loading = true;
    this.http
      .get<FeatureFlagsResponse>('/api/feature-flags')
      .pipe(
        map(response => ({
          feature_flags: response?.feature_flags ?? {},
          dynamicRatingBackfill: response?.dynamic_rating_backfill,
        })),
        takeUntil(this.onDestroy$)
      )
      .subscribe({
        next: ({ feature_flags, dynamicRatingBackfill }) => {
          const hasDeclarationPageFlag = Object.prototype.hasOwnProperty.call(feature_flags, 'DECLARATION_PAGE');
          const declarationPageEnabled = hasDeclarationPageFlag
            ? Boolean(feature_flags['DECLARATION_PAGE'])
            : this.tenantFeatureSvc.isTenantFeatureEnabled(this.tenantSvc.tenantId, TenantFeature.DECLARATION_PAGE);

          this.dynamicRatingWasEnabled = Boolean(feature_flags['DYNAMIC_RATING']);

          this.flagsForm.patchValue(
            {
              declarationPage: declarationPageEnabled,
              dynamicRating: this.dynamicRatingWasEnabled,
              customRoles: Boolean(feature_flags['CUSTOM_ROLES']),
              batchWarrantyPayments: Boolean(feature_flags['BATCH_WARRANTY_PAYMENTS']),
              claimZeroPay: Boolean(feature_flags['CLAIM_ZERO_PAY']),
              newClaimsFlow: Boolean(feature_flags[NEW_CLAIMS_FLOW_FLAG]),
            },
            { emitEvent: false }
          );

          this.flagsForm.markAsPristine();
          this.loading = false;
          this.handleBackfillStatus(dynamicRatingBackfill, false);
        },
        error: () => {
          this.loading = false;
          this.toastSvc.show({
            type: 'error',
            title: 'Failed to load feature flags',
            text: 'Please try again or contact support.',
          });
        },
      });
  }

  async saveFlags() {
    if (!this.flagsForm.dirty || this.saving || this.dynamicRatingBackfillProcessing) return;

    this.saving = true;
    const enablingDynamicRating = !this.dynamicRatingWasEnabled && Boolean(this.flagsForm.value.dynamicRating);

    const flagPayload = {
      feature_flags: {
        DECLARATION_PAGE: Boolean(this.flagsForm.value.declarationPage),
        DYNAMIC_RATING: Boolean(this.flagsForm.value.dynamicRating),
        CUSTOM_ROLES: Boolean(this.flagsForm.value.customRoles),
        BATCH_WARRANTY_PAYMENTS: Boolean(this.flagsForm.value.batchWarrantyPayments),
        CLAIM_ZERO_PAY: Boolean(this.flagsForm.value.claimZeroPay),
        [NEW_CLAIMS_FLOW_FLAG]: Boolean(this.flagsForm.value.newClaimsFlow),
      },
    };

    let featureFlagsUpdated = false;
    let backfillStatus: DynamicRatingBackfillStatus | undefined;

    try {
      const response = await firstValueFrom(this.http.post<FeatureFlagsResponse>('/api/feature-flags', flagPayload));
      this.dynamicRatingWasEnabled = Boolean(response?.feature_flags?.['DYNAMIC_RATING']);
      this.flagsForm.patchValue(
        {
          declarationPage: Boolean(response?.feature_flags?.['DECLARATION_PAGE']),
          dynamicRating: this.dynamicRatingWasEnabled,
          customRoles: Boolean(response?.feature_flags?.['CUSTOM_ROLES']),
          batchWarrantyPayments: Boolean(response?.feature_flags?.['BATCH_WARRANTY_PAYMENTS']),
          claimZeroPay: Boolean(response?.feature_flags?.['CLAIM_ZERO_PAY']),
          newClaimsFlow: Boolean(response?.feature_flags?.[NEW_CLAIMS_FLOW_FLAG]),
        },
        { emitEvent: false }
      );
      featureFlagsUpdated = true;
      backfillStatus = response?.dynamic_rating_backfill?.status;

      if (enablingDynamicRating && !backfillStatus) {
        backfillStatus = 'processing';
        this.handleBackfillStatus({ status: 'processing' });
      } else if (response?.dynamic_rating_backfill) {
        this.handleBackfillStatus(response.dynamic_rating_backfill);
      }
    } catch {
      this.toastSvc.show({
        type: 'error',
        title: 'Failed to update feature flags',
        text: 'Please try again or contact support.',
      });
    }

    if (featureFlagsUpdated) {
      this.flagsForm.markAsPristine();

      if (enablingDynamicRating && backfillStatus === 'processing') {
        this.toastSvc.show({
          type: 'success',
          title: 'Feature flags updated',
          text: 'Dynamic Rating is enabled. Rating Matrix configuration is running in the background.',
        });
      } else if (enablingDynamicRating && backfillStatus === 'completed') {
        this.showDynamicRatingSuccessBanner();
        this.toastSvc.show({
          type: 'success',
          title: 'Feature flags updated',
          text: 'Dynamic Rating is enabled and Rating Matrix configuration is complete.',
        });
      } else if (enablingDynamicRating && backfillStatus === 'failed') {
        this.toastSvc.show({
          type: 'error',
          title: 'Feature flags updated',
          text: 'Dynamic Rating is enabled, but Rating Matrix configuration failed. Please contact support.',
        });
      } else {
        this.toastSvc.show({
          type: 'success',
          title: 'Feature flags updated',
          text: 'Your changes have been saved.',
        });
      }

      // Refresh the centralized feature flags service
      this.featureFlagsSvc.refresh();
    }

    this.saving = false;
  }

  private handleBackfillStatus(state?: DynamicRatingBackfillState, notifyOnTerminal = true) {
    const wasProcessing = this.dynamicRatingBackfillProcessing;
    this.applyBackfillState(state);

    if (state?.status === 'processing') {
      this.clearDynamicRatingSuccessBanner();
      this.startDynamicRatingBackfillPolling();
      return;
    }

    this.stopDynamicRatingBackfillPolling();

    if (!notifyOnTerminal || !wasProcessing) return;

    if (state?.status === 'completed') {
      this.showDynamicRatingSuccessBanner();
      this.toastSvc.show({
        type: 'success',
        title: 'Rating Matrix configuration complete',
        text: 'Plan and term configuration has finished processing.',
      });
      return;
    }

    if (state?.status === 'failed') {
      this.toastSvc.show({
        type: 'error',
        title: 'Rating Matrix configuration failed',
        text: state.error || 'Please try again or contact support.',
      });
    }
  }

  private applyBackfillState(state?: DynamicRatingBackfillState) {
    const status = state?.status ?? 'idle';

    this.dynamicRatingBackfillStatus = status;
    this.dynamicRatingBackfillStartedAt = state?.startedAt;
    this.dynamicRatingBackfillError = state?.error;
    this.dynamicRatingBackfillProcessing = status === 'processing';

    if (status === 'processing') {
      this.startDynamicRatingElapsedTimer();
      this.cdr.markForCheck();
      return;
    }

    this.stopDynamicRatingElapsedTimer();
    this.dynamicRatingBackfillElapsed = '';
    this.cdr.markForCheck();
  }

  private startDynamicRatingElapsedTimer(): void {
    this.stopDynamicRatingElapsedTimer();
    this.updateDynamicRatingElapsed();

    this.dynamicRatingElapsedInterval = setInterval(() => {
      this.updateDynamicRatingElapsed();
    }, 1000);
  }

  private stopDynamicRatingElapsedTimer(): void {
    if (this.dynamicRatingElapsedInterval) {
      clearInterval(this.dynamicRatingElapsedInterval);
      this.dynamicRatingElapsedInterval = null;
    }
  }

  private showDynamicRatingSuccessBanner(): void {
    this.clearDynamicRatingSuccessBanner();
    this.dynamicRatingBackfillShowSuccess = true;
    this.dynamicRatingBackfillSuccessTimeout = setTimeout(() => {
      this.dynamicRatingBackfillShowSuccess = false;
      this.dynamicRatingBackfillSuccessTimeout = null;
      this.cdr.markForCheck();
    }, 8000);
  }

  private clearDynamicRatingSuccessBanner(): void {
    if (this.dynamicRatingBackfillSuccessTimeout) {
      clearTimeout(this.dynamicRatingBackfillSuccessTimeout);
      this.dynamicRatingBackfillSuccessTimeout = null;
    }
    this.dynamicRatingBackfillShowSuccess = false;
  }

  private updateDynamicRatingElapsed(): void {
    if (!this.dynamicRatingBackfillStartedAt) {
      this.dynamicRatingBackfillElapsed = '';
      this.cdr.markForCheck();
      return;
    }

    const elapsedMs = Math.max(0, Date.now() - new Date(this.dynamicRatingBackfillStartedAt).getTime());
    const totalSeconds = Math.floor(elapsedMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    this.dynamicRatingBackfillElapsed =
      minutes > 0 ? `${minutes}m ${seconds.toString().padStart(2, '0')}s` : `${seconds}s`;
    this.cdr.markForCheck();
  }

  private startDynamicRatingBackfillPolling(): void {
    this.stopDynamicRatingBackfillPolling();

    this.dynamicRatingBackfillPollingSubscription = timer(
      this.BACKFILL_POLL_INTERVAL_MS,
      this.BACKFILL_POLL_INTERVAL_MS
    )
      .pipe(
        take(this.BACKFILL_POLL_MAX_ATTEMPTS),
        takeUntil(this.onDestroy$),
        switchMap(() =>
          this.http
            .get<FeatureFlagsResponse>('/api/feature-flags')
            .pipe(catchError(() => of(null as FeatureFlagsResponse | null)))
        ),
        tap(response => {
          if (!response || response.dynamic_rating_backfill?.status !== 'processing') return;
          this.applyBackfillState(response.dynamic_rating_backfill);
        }),
        filter(
          (response): response is FeatureFlagsResponse =>
            !!response && response.dynamic_rating_backfill?.status !== 'processing'
        ),
        take(1)
      )
      .subscribe({
        next: response => this.handleBackfillStatus(response.dynamic_rating_backfill),
        complete: () => {
          if (this.onDestroy$.closed || !this.dynamicRatingBackfillProcessing) return;

          this.applyBackfillState({
            status: 'failed',
            error: 'Configuration timed out while waiting for completion.',
          });
          this.toastSvc.show({
            type: 'error',
            title: 'Rating Matrix configuration is taking longer than expected',
            text: 'Configuration may still be running in the background. Refresh this page later to check status.',
          });
        },
      });
  }

  private stopDynamicRatingBackfillPolling(): void {
    this.dynamicRatingBackfillPollingSubscription?.unsubscribe();
    this.dynamicRatingBackfillPollingSubscription = null;
  }
}
